import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showError, showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  BlacklistedEmail,
  createBlockedEmail,
  deleteBlockedEmail,
  loadBlacklistedEmails,
  updateBlockedEmail,
} from './requests';

const BlacklistedEmailsQueryKey = () => ['blacklisted-emails'];

export const useInvalidateBlacklistedEmails = () => {
  const queryClient = useQueryClient();
  const invalidateEmails = () => queryClient.invalidateQueries(BlacklistedEmailsQueryKey());
  return { invalidateEmails };
};

export const useGetBlackListedEmails = () => {
  return useQuery({
    queryKey: BlacklistedEmailsQueryKey(),
    queryFn: loadBlacklistedEmails,
  });
};

export const useCreateBlacklistedEmail = () => {
  const { t } = useScopedTranslate(Locale);
  const { invalidateEmails } = useInvalidateBlacklistedEmails();

  return useMutation((props: BlacklistedEmail) => createBlockedEmail(props), {
    onSuccess: () => {
      showSuccess(t('blockedEmailSaved'));
      invalidateEmails();
    },
    onError: () => {
      showError(t('blockedEmailError'));
    },
  });
};

export function useUpdateBlacklistedEmail() {
  const { t } = useScopedTranslate(Locale);
  const { invalidateEmails } = useInvalidateBlacklistedEmails();

  return useMutation((props: BlacklistedEmail) => updateBlockedEmail(props), {
    onSuccess: (_) => {
      invalidateEmails();
      showSuccess(t('blockedEmailSaved'));
    },
    onError: () => {
      showError(t('blockedEmailError'));
    },
  });
}

export function useRemoveBlockedEmail() {
  const { t } = useScopedTranslate(Locale);
  const { invalidateEmails } = useInvalidateBlacklistedEmails();

  return useMutation((id: number) => deleteBlockedEmail(id), {
    onSuccess: () => {
      showSuccess(t('blockedEmailRemoved'));
      invalidateEmails();
    },
    onError: () => {
      showError(t('errorOndeleteBlocked'));
    },
  });
}

const Locale = {
  en: {
    blockedEmailSaved: 'Blocked email saved',
    blockedEmailRemoved: 'Blocked email removed',
    blockedEmailError: 'Error saving blocked email',
    errorOndeleteBlocked: 'Error deleting blocked email',
  },
  is: {
    blockedEmailSaved: 'Netfang vistað',
    blockedEmailRemoved: 'Netfang fjarlægt',
    blockedEmailError: 'Villa við að vista bannað netfang',
    errorOndeleteBlocked: 'Villa við að eyða netfang',
  },
};
